import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import cls from 'classnames';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';

import { IMG_WITH_TEXT_SIZES } from '../../landingPages/constants';

import useStyles from './styles';
import ICarousel from './types';

const DRAG_LIMIT = 100;

function getMarginLeft({ width, gap, i }: { width: number; gap: number; i: number }) {
  if (i === 0) {
    return 2 * gap;
  }
  const itemWidth = width - 4 * gap;

  return -(i * itemWidth - gap + (i - 1) * gap);
}

function Carousel({ className, items, initial = 0, showIndicator = true, gap = 8 }: ICarousel) {
  const classes = useStyles();
  const { length: itemsCount } = items;
  const containerRef = useRef<HTMLDivElement>();
  const [currentIndex, setCurrentIndex] = useState(initial);
  const [width, setWidth] = useState(0);
  const { width: layoutWidth } = useWindowSize();

  useLayoutEffect(() => {
    setWidth(containerRef.current?.offsetWidth || 0);
  }, [containerRef, layoutWidth]);

  const [springProps] = useSpring(
    () => ({
      marginLeft: getMarginLeft({ width, gap, i: currentIndex }),
    }),
    [currentIndex],
  );

  useEffect(() => {
    setCurrentIndex(initial);
  }, [initial]);

  const bindSwipeGesture = useDrag(
    ({ active, movement: [movX], direction: [dirX], cancel }) => {
      if (active && Math.abs(movX) > DRAG_LIMIT) {
        setCurrentIndex((prev) => {
          const increment = dirX > 0 ? -1 : 1;
          const nextRawIndex = (prev + increment) % itemsCount;

          return nextRawIndex >= 0 ? nextRawIndex : itemsCount + nextRawIndex;
        });
        cancel();
      }
    },
    {
      pointer: { touch: true },
    },
  ) as any;

  return (
    <div className={cls(classes.root, className)} ref={containerRef}>
      <animated.div
        {...bindSwipeGesture()}
        style={{
          ...springProps,
          gridTemplateColumns: `repeat(${itemsCount}, ${width - 4 * gap}px)`,
          gap,
        }}
        className={classes.container}
      >
        {items.map(({ alt, content, src, mobileSrc }, i) => (
          <div className={classes.page} key={i}>
            <img
              className={cls(classes.image, className)}
              src={src}
              alt={alt}
              sizes={IMG_WITH_TEXT_SIZES}
              srcSet={`${mobileSrc} 327w, ${src} 648w`}
            />
            {content}
          </div>
        ))}
      </animated.div>

      {showIndicator && (
        <div className={classes.indicatorBack}>
          <div
            className={classes.indicatorFront}
            style={{
              width: `${100 / itemsCount}%`,
              marginLeft: `${currentIndex * (100 / itemsCount)}%`,
            }}
          ></div>
        </div>
      )}
    </div>
  );
}

export default Carousel;
